import { useRef, useState } from 'react';
import { useResizeObserver, useEventListener } from '@react-hookz/web';

/**
 * Autoscroll, stated as two declarative DOM subscriptions (react-hookz):
 *   • useResizeObserver — follow any height change to the message container
 *   • useEventListener('scroll') — stop following if the user scrolls up
 *
 * Listens on the scroll-area viewport directly, so it never triggers
 * scrollIntoView's ancestor-walking. Also used by the operator chat panel.
 */
export function useStickToBottom(): (node: HTMLDivElement | null) => void {
  const contentRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<HTMLElement | null>(null);
  const stickToBottom = useRef(true);

  const setContentNode = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    setViewport(
      node?.closest<HTMLElement>('[data-slot="scroll-area-viewport"]') ?? null,
    );
  };

  useEventListener(viewport, 'scroll', () => {
    if (!viewport) return;
    stickToBottom.current =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 80;
  });

  useResizeObserver(contentRef, () => {
    if (viewport && stickToBottom.current) {
      viewport.scrollTo({ top: viewport.scrollHeight });
    }
  });

  return setContentNode;
}
